import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, PenTool, Ruler, TreeDeciduous, PackageCheck } from "lucide-react";
import { getProducts } from "@/lib/api";
import { IMG, CATEGORIES } from "@/data/site";
import DivisionHero from "@/components/DivisionHero";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker } from "@/components/Reveal";

const PROCESS = [
  { icon: TreeDeciduous, title: "Choose species", copy: "Kwila / Merbau or selected tropical hardwoods to suit internal or external use." },
  { icon: Ruler, title: "Confirm dimensions", copy: "Standard sizes or custom — height, width, thickness and any hardware requirements." },
  { icon: PenTool, title: "Send plans & quote", copy: "Upload-ready quote flow for builders and specifiers — drawings can follow by email." },
  { icon: PackageCheck, title: "Manufacture & deliver", copy: "Made in PNG, distributed from Archerfield with freight arranged to your site." },
];

export default function Doors() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts({ division: "doors" }).then(setProducts);
  }, []);

  return (
    <div data-testid="doors-page">
      <DivisionHero
        kicker="Division 02 — Doors"
        lines={["Solid Timber Doors,", "Made to Last"]}
        intro="Explore solid timber door options manufactured from premium hardwoods. Suitable for builders, renovators, designers, specifiers and trade supply — standard sizes or fully custom, every door starts with a quote."
        image="/doors-hero.png"
        imageAlt="Large geometric timber door wall in a café interior"
        brochure={{ label: "Mr Kwila Doors — Product Brochure", pdf: "/brochures/mr-kwila-doors-brochure.pdf", cover: "/brochures/doors-cover.png" }}
      />

      <section className="px-5 pb-24 md:px-10" data-testid="doors-split">
        <div className="mx-auto grid max-w-[1500px] gap-6 md:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col justify-between rounded-sm bg-ink p-8 text-cream md:p-10">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-kwila">Standard Range</p>
                <h2 className="mt-3 font-display text-3xl md:text-4xl">Ready-to-quote solid doors</h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-cream/70">
                  Solid Kwila entry doors and internal doors in standard dimensions. Add to your quote list and
                  we will confirm pricing, lead time and delivery.
                </p>
              </div>
              <Link to="/shop?division=doors" data-testid="doors-browse-standard" className="mt-8 flex w-fit items-center gap-2 rounded-full bg-cream px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-kwila hover:text-cream">
                Browse Doors <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex h-full flex-col justify-between rounded-sm border border-ink/15 p-8 md:p-10">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-kwila">Custom & Specifier</p>
                <h2 className="mt-3 font-display text-3xl md:text-4xl">Made to your drawings</h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/65">
                  Custom sizes, species and finishes for builders, architects and door manufacturers. Share plans
                  and schedules for a project price. [Compliance, fire and acoustic rating details to be confirmed
                  by client.]
                </p>
              </div>
              <Link to="/request-quote" data-testid="doors-custom-quote" className="mt-8 flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila">
                Start a Door Quote <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-stone/60 px-5 py-24 md:px-10" data-testid="doors-categories">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-8">
            <Kicker>Door Categories</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">Find your door</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.doors.map((c) => (
              <Link
                key={c}
                to={`/shop?division=doors&category=${encodeURIComponent(c)}`}
                data-testid={`doors-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-full border border-ink/20 bg-cream px-6 py-3 text-sm font-semibold transition-colors hover:bg-ink hover:text-cream"
              >
                {c}
              </Link>
            ))}
          </div>
          <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-24 md:px-10" data-testid="doors-process">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12">
            <Kicker>How It Works</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
              From plan to front door
            </h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-4">
            {PROCESS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="border-t border-ink/15 pt-6">
                <div className="flex items-center justify-between">
                  <s.icon size={24} className="text-kwila" strokeWidth={1.5} />
                  <span className="font-display text-4xl font-light text-outline">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-display text-2xl">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA
        title="Quoting doors for a build or renovation?"
        copy="Send door types, dimensions, species and finish preferences. Builders and specifiers can include plan schedules — our team prices project volumes."
      />
    </div>
  );
}
